import { useEffect, useState } from "react";
import type { OwnerFilter } from "../types";
import { readReportingScope } from "../lib/reporting";
import { todayIn } from "../lib/finance";
import { routeView, type View } from "./views";

const param = (name: string, fallback: string) =>
  () => new URLSearchParams(window.location.search).get(name) || fallback;

/**
 * The month and the transaction filters. They start from the address bar, follow the browser's
 * back and forward buttons, and are written back to the address bar as they change, so a filtered
 * list can be shared or reloaded.
 */
export function useTransactionFilters({ setView, setReviewUncategorized, owner, setOwner }: {
  owner: OwnerFilter; setOwner: (owner: OwnerFilter) => void;
  setView: (view: View) => void;
  setReviewUncategorized: (active: boolean) => void;
}) {
  const [month, setMonth] = useState(() => new URLSearchParams(window.location.search).get("month") || todayIn().slice(0, 7));
  const [query, setQuery] = useState(param("q", ""));
  const [dateFilter, setDateFilter] = useState(param("date", ""));
  const [accountFilter, setAccountFilter] = useState(param("account", "All"));
  const [category, setCategoryValue] = useState(param("category", "All"));
  const [subcategory, setSubcategory] = useState<string | null>(null);
  const [type, setType] = useState(param("type", "All"));
  const [tripFilter, setTripFilter] = useState(param("trip", "All"));
  const [reporting, setReporting] = useState(() => readReportingScope(new URLSearchParams(window.location.search)));
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    const syncRoute = () => {
      setView(routeView());
      const p = new URLSearchParams(window.location.search);
      setReviewUncategorized(p.get("categorize") === "1");
      setMonth(p.get("month") || todayIn().slice(0, 7));
      setReporting(readReportingScope(p));
      if (readReportingScope(p) && p.has("reportOwner")) setOwner(p.get("reportOwner")!);
      setQuery(p.get("q") || "");
      setDateFilter(p.get("date") || "");
      setAccountFilter(p.get("account") || "All");
      setCategoryValue(p.get("category") || "All");
      setType(p.get("type") || "All");
      setTripFilter(p.get("trip") || "All");
    };
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  // Registers the popstate listener once; the setters are stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const url = new URL(window.location.href);
    for (const [key, value] of Object.entries({
      month,
      q: query,
      date: dateFilter,
      account: accountFilter,
      category,
      type,
      trip: tripFilter,
    })) {
      if (value && value !== "All") url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    }
    if (reporting) url.searchParams.set("reportOwner", owner);
    else url.searchParams.delete("reportOwner");
    if (reporting) url.searchParams.set("report", reporting.activity);
    else url.searchParams.delete("report");
    if (reporting?.merchant !== undefined) url.searchParams.set("merchant", reporting.merchant);
    else url.searchParams.delete("merchant");
    window.history.replaceState(window.history.state, "", url);
  }, [month, query, dateFilter, accountFilter, category, type, tripFilter, reporting, owner]);
  useEffect(() => {
    if (dateFilter && !dateFilter.startsWith(month)) setDateFilter("");
  }, [month, dateFilter]);
  function setCategory(value: string) {
    setCategoryValue(value);
    setSubcategory(null);
  }
  function changeMonth(delta: number) {
    const d = new Date(month + "-01T12:00:00");
    d.setMonth(d.getMonth() + delta);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return {
    month, setMonth, changeMonth,
    query, setQuery, dateFilter, setDateFilter, accountFilter, setAccountFilter,
    category, setCategory, subcategory, setSubcategory, type, setType,
    tripFilter, setTripFilter, reporting, setReporting,
    filtersOpen, setFiltersOpen,
  };
}
